import { useState, useRef, forwardRef, useImperativeHandle } from 'react'
import { RichContentEditor } from './RichContentEditor'

// Widget types that show a rich content editor
const RICH_CONTENT_TYPES = new Set(['rich_content', 'custom_html', 'project_description', 'disclaimer'])

// Human-readable labels for widget types
const TYPE_LABELS = {
  line_chart: 'Line Chart',
  smooth_line_chart: 'Smooth Line Chart',
  combo_chart: 'Combo Chart',
  area_chart: 'Area Chart',
  column_chart: 'Column Chart',
  stacked_column_chart: 'Stacked Column Chart',
  bar_chart: 'Bar Chart',
  pie_chart: 'Pie Chart',
  doughnut_chart: 'Doughnut Chart',
  pie_chart_3d: '3D Pie Chart',
  wind_rose: 'Wind Rose',
  gauge_series: 'Gauge Series',
  bullet_graph: 'Bullet Graph',
  solid_gauge: 'Solid Gauge',
  air_temperature: 'Air Temperature',
  barometric_pressure: 'Barometric Pressure',
  daily_rain: 'Daily Rain',
  heading: 'Heading',
  humidity: 'Humidity',
  interval_rain: 'Interval Rain',
  lightning: 'Lightning',
  precipitation: 'Precipitation',
  pressure: 'Pressure',
  rain_intensity: 'Rain Intensity',
  solar_radiation: 'Solar Radiation',
  total_rain: 'Total Rain',
  wind: 'Wind',
  ammonium: 'Ammonium',
  bga: 'BGA',
  chlorophyll: 'Chlorophyll',
  conductivity: 'Conductivity',
  dissolved_oxygen: 'Dissolved Oxygen',
  hydrocarbons: 'Hydrocarbons',
  level: 'Level',
  oil_detection: 'Oil Detection',
  orp: 'ORP',
  ph: 'pH',
  pressure_water: 'Water Pressure',
  salinity: 'Salinity',
  snow: 'Snow',
  sp_conductance: 'Sp. Conductance',
  temperature: 'Temperature',
  turbidity: 'Turbidity',
  water_current: 'Water Current',
  wave: 'Wave',
  soil_conductivity: 'Soil Conductivity',
  soil_moisture: 'Soil Moisture',
  soil_salinity: 'Soil Salinity',
  soil_temperature: 'Soil Temperature',
  rich_content: 'Rich Content',
  custom_html: 'Custom HTML',
  project_description: 'Project Description',
  project_photo: 'Project Photo',
  field_notes: 'Field Notes',
  gallery: 'Gallery',
  webcam: 'Webcam',
  data_table: 'Data Table',
  live_data: 'Live Data',
  site_conditions: 'Site Conditions',
  gps: 'GPS',
  alarm_summary: 'Alarm Summary',
  individual_alarm: 'Individual Alarm',
  environmental_news: 'Environmental News',
  event_monitor: 'Event Monitor',
  knowledge_base: 'Knowledge Base',
  support_project: 'Support Our Project',
  disclaimer: 'Disclaimer',
}

function typeLabel(widgetType) {
  return TYPE_LABELS[widgetType] || (widgetType || 'Widget').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// ── Gallery image editor ──────────────────────────────────────────────────────
const GalleryEditor = forwardRef(function GalleryEditor({ images, onChange }, ref) {
  const [addMode, setAddMode] = useState(null) // null | 'upload' | 'url'
  const [urlInput, setUrlInput]     = useState('')
  const [captionInput, setCaptionInput] = useState('')
  const [uploadPreview, setUploadPreview] = useState(null)
  const fileRef = useRef()

  // Expose getImages() so the parent modal can flush any pending (not-yet-added) image on save
  useImperativeHandle(ref, () => ({
    getImages: () => {
      const src = addMode === 'url' ? urlInput.trim() : uploadPreview
      if (addMode && src) {
        return [...images, { src, alt: captionInput.trim() }]
      }
      return images
    }
  }), [addMode, urlInput, uploadPreview, captionInput, images])

  const resetForm = () => {
    setAddMode(null)
    setUrlInput('')
    setCaptionInput('')
    setUploadPreview(null)
  }

  const readFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      setUploadPreview(ev.target.result)
      setCaptionInput(prev => prev || file.name.replace(/\.[^.]+$/, ''))
    }
    reader.readAsDataURL(file)
    setAddMode('upload')
  }

  const handleFileInput = (e) => readFile(e.target.files?.[0])

  const handleDrop = (e) => {
    e.preventDefault()
    readFile(e.dataTransfer.files?.[0])
  }

  const handleAddImage = () => {
    const src = addMode === 'url' ? urlInput.trim() : uploadPreview
    if (!src) return
    onChange([...images, { src, alt: captionInput.trim() }])
    resetForm()
  }

  const handleRemove = (idx) => onChange(images.filter((_, i) => i !== idx))

  const canAdd = addMode === 'url' ? !!urlInput.trim() : !!uploadPreview

  return (
    <div className="flex flex-col gap-3">

      {/* Existing images grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style={{ aspectRatio: '1' }}>
              <img src={img.src} alt={img.alt || ''} className="w-full h-full object-cover" />
              {img.alt && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1.5 py-0.5 truncate">
                  {img.alt}
                </div>
              )}
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center leading-none"
              >✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Add image panel */}
      {addMode === null ? (
        <div
          className="border-2 border-dashed border-gray-200 rounded-lg p-5 text-center cursor-pointer hover:border-[#358ECD] hover:bg-blue-50/30 transition select-none"
          onClick={() => fileRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
        >
          <span className="text-2xl block mb-1">🖼️</span>
          <p className="text-sm text-gray-500 mb-3">Click to browse · or drag & drop an image here</p>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={e => { e.stopPropagation(); fileRef.current?.click() }}
              className="text-xs px-3 py-1.5 bg-[#358ECD] text-white rounded-md hover:bg-[#2d7ab5] transition"
            >
              Browse Files
            </button>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setAddMode('url') }}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition"
            >
              Image URL
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileInput} />
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-col gap-3">

          {/* Upload tab: preview */}
          {addMode === 'upload' && (
            <>
              {uploadPreview ? (
                <div className="relative">
                  <img src={uploadPreview} alt="preview" className="max-h-40 w-full object-contain rounded-md bg-white border border-gray-200" />
                  <button
                    type="button"
                    onClick={() => { setUploadPreview(null); fileRef.current.value = '' }}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                  >✕</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full py-8 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-400 hover:border-[#358ECD] hover:text-[#358ECD] transition"
                >
                  Click to select a file
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileInput} />
            </>
          )}

          {/* URL tab */}
          {addMode === 'url' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-600">Image URL</label>
              <input
                type="text"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#358ECD]/40 focus:border-[#358ECD]"
                placeholder="https://example.com/image.jpg"
                autoFocus
              />
              {urlInput.trim() && (
                <img
                  src={urlInput.trim()}
                  alt="preview"
                  className="mt-1 max-h-32 w-full object-contain rounded-md bg-white border border-gray-200"
                  onError={e => { e.target.style.display = 'none' }}
                  onLoad={e => { e.target.style.display = '' }}
                />
              )}
            </div>
          )}

          {/* Caption */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-600">Caption <span className="font-normal text-gray-400">(optional)</span></label>
            <input
              type="text"
              value={captionInput}
              onChange={e => setCaptionInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && canAdd) handleAddImage() }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#358ECD]/40 focus:border-[#358ECD]"
              placeholder="Image caption or label"
            />
          </div>

          <div className="flex items-center justify-between">
            {/* Switch between upload / url */}
            <button
              type="button"
              onClick={() => { setAddMode(addMode === 'url' ? 'upload' : 'url'); setUploadPreview(null); setUrlInput('') }}
              className="text-xs text-[#358ECD] hover:underline"
            >
              {addMode === 'url' ? 'Switch to file upload' : 'Switch to URL instead'}
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="text-xs px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddImage}
                disabled={!canAdd}
                className="text-xs px-3 py-1.5 bg-[#358ECD] text-white rounded-md hover:bg-[#2d7ab5] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Add Image
              </button>
            </div>
          </div>
        </div>
      )}

      {images.length > 0 && (
        <p className="text-xs text-gray-400">{images.length} image{images.length !== 1 ? 's' : ''} · hover a thumbnail to remove it</p>
      )}
    </div>
  )
})

// ── Parse gallery images stored as JSON in rich_content ───────────────────────
function parseGalleryImages(raw) {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// ── Main modal ────────────────────────────────────────────────────────────────
export function WidgetConfigModal({ widget, onClose, onSaved }) {
  const widgetType    = widget?.widget_type || 'custom'
  const isRichContent = RICH_CONTENT_TYPES.has(widgetType)
  const isGallery     = widgetType === 'gallery'
  const label         = typeLabel(widgetType)

  const [title, setTitle]           = useState(widget?.custom_title ?? widget?.title ?? '')
  const [description, setDescription] = useState(widget?.custom_description ?? widget?.description ?? '')
  const [content, setContent]       = useState(widget?.custom_rich_content ?? widget?.rich_content ?? '')

  // Gallery images — parsed from JSON stored in rich_content
  const [galleryImages, setGalleryImages] = useState(() =>
    parseGalleryImages(widget?.custom_rich_content ?? widget?.rich_content ?? '')
  )
  // Ref to GalleryEditor so we can flush any pending (not-yet-added) image on save
  const galleryEditorRef = useRef(null)

  const handleSave = () => {
    if (!widget?.instance_id) { onClose(); return }
    // Auto-flush any image that's in the "add form" but not yet committed via "Add Image"
    const finalImages = isGallery && galleryEditorRef.current
      ? galleryEditorRef.current.getImages()
      : galleryImages
    const richContent = isGallery
      ? JSON.stringify(finalImages)
      : content
    onSaved?.({ instance_id: widget.instance_id, title, description, rich_content: richContent })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-2xl max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Configure Widget</h2>
            <p className="text-xs text-[#358ECD] mt-0.5">{widget?.custom_title || widget?.title || label}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 flex flex-col gap-5">

          {/* Widget type badge */}
          <div className="flex items-center gap-2">
            {widget?.icon && <span className="text-xl">{widget.icon}</span>}
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-[#358ECD] border border-blue-100">
              {label}
            </span>
          </div>

          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#358ECD]/40 focus:border-[#358ECD]"
              placeholder="Widget title"
            />
          </div>

          {/* Description — all widget types */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">
              Description <span className="font-normal text-gray-400">(optional subtitle)</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#358ECD]/40 focus:border-[#358ECD]"
              placeholder="Short description shown below the title"
            />
          </div>

          {/* Gallery image editor */}
          {isGallery && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Images</label>
              <GalleryEditor ref={galleryEditorRef} images={galleryImages} onChange={setGalleryImages} />
            </div>
          )}

          {/* Rich content editor — only for HTML / content widget types */}
          {isRichContent && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Content</label>
              <RichContentEditor initialContent={content} onChange={setContent} />
            </div>
          )}

          <p className="text-xs text-gray-400">
            Changes apply to this widget instance only. Click &ldquo;Save Customization&rdquo; in the toolbar to persist.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2 text-sm rounded-md bg-[#358ECD] text-white hover:bg-[#2d7ab5]"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  )
}
